// CreditUnionsPage.jsx
import React, { useState } from "react";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Link,
  Button,
  Heading,
  Stack,
  List,
  ListItem,
  Icon,
  useDisclosure,
  Collapse,
  chakra,
} from "@chakra-ui/react";

// Brand tokens (you can move these to theme.extend)
const akru = {
  orange: "#FD8534",
  orangeHover: "#FD8534CC",
  orangeLight: "#FFF3EB",
  white: "#FFF",
  offWhite: "#F9F9F9",
  grayText: "#666",
  darkText: "#272828",
  borderGray: "#CCC",
  radius: "5px",
  shadow: "0px 4px 30px rgba(0,0,0,0.15)",
  maxW: "12000px",
};

const Dropdown = ({ label, items }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <Box position="relative" onMouseEnter={onOpen} onMouseLeave={onClose}>
      <Flex
        align="center"
        gap="7px"
        cursor="pointer"
        color={akru.grayText}
        fontSize="18px"
        fontWeight="500"
        _hover={{ color: akru.orange }}
        transition="color .3s ease"
      >
        <Text>{label}</Text>
        <Box
          as="svg"
          w="10px"
          h="8px"
          viewBox="0 0 10 8"
          fill="none"
          transition="transform .3s ease"
          transform={isOpen ? "rotate(180deg)" : "none"}
        >
          <chakra.path
            d="M1 1L5 6L9 1"
            stroke={akru.grayText}
            strokeWidth="1.5"
          />
        </Box>
      </Flex>

      <Collapse in={isOpen} animateOpacity style={{ position: "absolute", left: 0, right: "auto" }}>
        <Box
          mt="10px"
          bg={akru.white}
          minW="280px"
          boxShadow={akru.shadow}
          borderRadius={akru.radius}
          zIndex={1001}
        >
          {items.map((item, idx) => (
            <Link
              key={idx}
              href={item.href}
              display="block"
              px="20px"
              py="12px"
              fontSize="16px"
              fontWeight="500"
              textDecoration="none"
              color={akru.grayText}
              _hover={{
                color: akru.orange,
                bg: akru.orangeLight,
              }}
              borderTopRadius={idx === 0 ? akru.radius : 0}
              borderBottomRadius={idx === items.length - 1 ? akru.radius : 0}
            >
              {item.label}
            </Link>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const PilotCard = ({ number, title, duration, bullets }) => (
  <Box
    p={{base:"20px 10px",md:"50px 40px"}}
    bg={akru.white}
    borderRadius={akru.radius}
    boxShadow={akru.shadow}
    transition="all .3s ease"
    _hover={{
      transform: "translateY(-5px)",
      boxShadow: "0px 8px 40px rgba(253, 133, 52, 0.2)",
    }}
  >
    <Text
      fontSize="20px"
      fontWeight="500"
      letterSpacing="2px"
      mb="20px"
      color={akru.orange}
      lineHeight="28px"
    >
      {number}
    </Text>

    <Heading
      as="h3"
      fontSize="20px"
      fontWeight="500"
      mb="15px"
      color={akru.darkText}
      lineHeight="28px"
    >
      {title}
    </Heading>

    <Text
      fontSize="14px"
      fontWeight="700"
      mb="25px"
      textTransform="uppercase"
      letterSpacing="1px"
      color={akru.grayText}
    >
      {duration}
    </Text>

    <List spacing="0" mb="35px">
      {bullets.map((b, i) => (
        <ListItem
          key={i}
          py="12px"
          fontSize="14px"
          lineHeight="28px"
          fontWeight="500"
          color={akru.grayText}
        >
          • {b}
        </ListItem>
      ))}
    </List>

    <Button
      as="a"
      href="#contact"
      bg={akru.orange}
      color={akru.white}
      fontFamily="var(--font-primary, Cera Pro, Inter, -apple-system, BlinkMacSystemFont, sans-serif)"
      fontWeight="700"
      fontSize="16px"
      borderRadius={akru.radius}
      px="26px"
      py="13px"
      _hover={{ bg: akru.orangeHover }}
    >
      Schedule a Pilot Discovery Call
    </Button>
  </Box>
);

const ProductCard = ({ icon, title, bullets }) => (
  <Box
    p="50px 35px"
    textAlign="center"
    bg={akru.white}
    borderRadius={akru.radius}
    border="1px solid"
    borderColor={akru.borderGray}
    boxShadow={akru.shadow}
    transition="all .3s ease"
    _hover={{
      transform: "translateY(-8px)",
      borderColor: akru.orange,
    }}
  >
    <Flex
      w="70px"
      h="70px"
      mb="30px"
      mx="auto"
      align="center"
      justify="center"
      bg={akru.orangeLight}
      borderRadius={akru.radius}
    >
      {icon}
    </Flex>

    <Heading
      as="h3"
      fontSize="20px"
      fontWeight="500"
      mb="20px"
      lineHeight="28px"
      color={akru.darkText}
    >
      {title}
    </Heading>

    <List
      spacing="12px"
      textAlign="left"
      fontSize="14px"
      lineHeight="28px"
      fontWeight="500"
      color={akru.grayText}
    >
      {bullets.map((b, i) => (
        <ListItem key={i}>• {b}</ListItem>
      ))}
    </List>
  </Box>
);

const ComplianceBadge = ({ icon, label }) => (
  <Box
    p="30px 20px"
    bg={akru.orangeLight}
    borderRadius={akru.radius}
    textAlign="center"
    transition="all .3s ease"
    _hover={{
      bg: akru.orange,
      color: akru.white,
      transform: "translateY(-5px)",
    }}
  >
    <Flex
      w="60px"
      h="60px"
      mx="auto"
      mb="15px"
      align="center"
      justify="center"
      bg={akru.white}
      borderRadius={akru.radius}
      _groupHover={{ bg: akru.orangeLight }}
    >
      {icon}
    </Flex>
    <Text
      fontSize="14px"
      fontWeight="600"
      color={akru.darkText}
      _groupHover={{ color: akru.white }}
    >
      {label}
    </Text>
  </Box>
);

const AkruCreditUnions = () => {
  const mobileNav = useDisclosure();

  return (
    <Box
      fontFamily="'Cera Pro', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
      bg={akru.white}
      color={akru.darkText}
      lineHeight="1.6"
    >

      {/* HERO */}
      <Box
        as="section"
        bgGradient={`linear(135deg, rgba(253,133,52,0.05) 0%, rgba(255,255,255,1) 100%)`}
        textAlign="center"
        color={akru.darkText}
        pt={{ base: "140px", md: "80px" }}
        pb={{ base: "100px", md: "130px" }}
      >
        <Box maxW={akru.maxW} mx="auto" px={{ base: "20px", md: "40px" }}>
            <Text
              fontSize="18px"
              fontWeight="700"
              letterSpacing="2px"
              textTransform="uppercase"
              mb="30px"
              color={akru.orange}
            >
              AKRU for Credit Unions
            </Text>

            <Heading
              as="h1"
              fontSize={{ base: "40px", md: "64px" }}
              fontWeight="500"
              lineHeight={{ base: "50px", md: "70px" }}
              mb="30px"
              color={akru.darkText}
            >
              Empowering Credit Unions to Modernize
              <br />
              <Box as="span" color={akru.orange}>
                Liquidity, Lending, and Member Engagement
              </Box>
            </Heading>

            <Text
              fontSize={{ base: "16px", md: "24px" }}
              fontWeight="500"
              lineHeight={{ base: "28px", md: "40px" }}
              maxW="800px"
              mx="auto"
              mb="50px"
              color={akru.grayText}
            >
              Purpose-built infrastructure enabling credit unions to unlock liquidity,
              expand lending capacity, and launch fintech partnerships — without
              replacing their core systems.
            </Text>

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing="20px"
              justify="center"
              flexWrap="wrap"
            >
              <Button
                as="a"
                href="#pilots"
                bg={akru.orange}
                color={akru.white}
                fontWeight="700"
                fontSize="16px"
                px="26px"
                py="13px"
                borderRadius={akru.radius}
                _hover={{ bg: akru.orangeHover }}
              >
                Explore Pilot Programs
              </Button>

              <Button
                as="a"
                href="#contact"
                bg={akru.white}
                color={akru.orange}
                fontWeight="700"
                fontSize="16px"
                px="26px"
                py="13px"
                borderRadius={akru.radius}
                border="2px solid"
                borderColor={akru.orange}
                _hover={{ bg: akru.orangeLight }}
              >
                Talk to Our Team
              </Button>
            </Stack>
        </Box>
      </Box>

      {/* CHALLENGE */}
      <Box
        as="section"
        bg={akru.offWhite}
        py={{ base: "80px", md: "130px" }}
      >
        <Box maxW={akru.maxW} mx="auto" px={{ base: "20px", md: "40px" }}>
          <Heading
            as="h2"
            fontSize={{ base: "40px", md: "64px" }}
            fontWeight="500"
            lineHeight={{ base: "50px", md: "70px" }}
            textAlign="center"
            color={akru.darkText}
            mb="30px"
          >
            Today's Credit Unions Face
            <br />
            Structural Constraints
          </Heading>

          <Text
            maxW="800px"
            mx="auto"
            textAlign="center"
            fontSize={{ base: "16px", md: "20px" }}
            lineHeight={{ base: "28px", md: "40px" }}
            fontWeight="500"
            color={akru.grayText}
            mb="50px"
          >
            Credit unions are navigating margin compression, rising liquidity
            pressures, and outdated technology that limits innovation. Traditional
            lending and participation markets are slow, illiquid, and
            capital-intensive — leaving valuable assets trapped on the balance
            sheet.
          </Text>

          <Grid
            mt="60px"
            gap="30px"
            templateColumns={{
              base: "1fr",
              sm: "repeat(auto-fit, minmax(240px,1fr))",
            }}
            maxW="1000px"
            mx="auto"
          >
            {[
              "Balance sheet lending caps and limited secondary-market liquidity",
              "Legacy core integration challenges",
              "High compliance costs and manual operations",
              "Growing member expectations for fintech-style services",
            ].map((text, i) => (
              <Box
                key={i}
                p="35px 30px"
                bg={akru.white}
                border="1px solid"
                borderColor={akru.borderGray}
                borderRadius={akru.radius}
                boxShadow={akru.shadow}
                fontSize="20px"
                lineHeight="28px"
                fontWeight="500"
                color={akru.grayText}
                transition="all .3s ease"
                _hover={{
                  transform: "translateY(-5px)",
                  borderColor: akru.orange,
                }}
              >
                {text}
              </Box>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* SOLUTIONS */}
      <Box as="section" bg={akru.white} py={{ base: "80px", md: "130px" }}>
        <Box maxW={akru.maxW} mx="auto" px={{ base: "20px", md: "40px" }}>
          <Heading
            as="h2"
            fontSize={{ base: "40px", md: "64px" }}
            fontWeight="500"
            lineHeight={{ base: "50px", md: "70px" }}
            textAlign="center"
            color={akru.darkText}
            mb="30px"
          >
            The Infrastructure Layer for
            <br />
            Cooperative Finance
          </Heading>

          <Text
            textAlign="center"
            fontSize={{ base: "16px", md: "20px" }}
            fontWeight="500"
            color={akru.grayText}
            maxW="768px"
            mx="auto"
            mb="60px"
            lineHeight={{ base: "28px", md: "40px" }}
          >
            Three integrated solutions designed to modernize credit union
            operations while maintaining regulatory compliance
          </Text>

          <Grid
            gap="40px"
            templateColumns={{ base: "1fr", md: "repeat(auto-fit, minmax(320px, 1fr))" }}
            mb="50px"
          >
            <ProductCard
              title="TreasuryConnect™"
              bullets={[
                "Enables programmable treasury and virtual sub-accounts for fintech partnerships",
                "Real-time settlement, reconciliation, and liquidity management",
              ]}
              icon={
                <Box
                  as="svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="34"
                  height="23"
                  viewBox="0 0 34 23"
                  fill="none"
                >
                  <chakra.path
                    d="M2 21H6C6.55 21 7 20.55 7 20V8C7 7.45 6.55 7 6 7H2C1.45 7 1 7.45 1 8V20C1 20.55 1.45 21 2 21ZM14 0C12.89 0 12.33 0.55 12.33 3C12.33 5.5 9.78 7.5 8 8.5V21C10 22 13.5 23 19 23H21C23.21 23 25.09 21.5 25.46 19.5L26.74 12.5C26.85 11.87 26.82 11.22 26.65 10.6C26.48 9.98 26.17 9.41 25.75 8.93C25.33 8.45 24.81 8.07 24.23 7.82C23.65 7.57 23.03 7.44 22.4 7.45H17.5C17.5 7.45 18.33 5.78 18.33 3C18.33 0 16 0 14 0Z"
                    fill={akru.orange}
                  />
                </Box>
              }
            />

            <ProductCard
              title="Small-Asset Tokenization"
              bullets={[
                "Fractionalize smaller loans and participations to free up capital and attract investors",
                "Unlock liquidity while maintaining compliance and servicing",
              ]}
              icon={
                <Box
                  as="svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <chakra.circle
                    cx="16"
                    cy="16"
                    r="10"
                    stroke={akru.orange}
                    strokeWidth="2"
                  />
                  <chakra.path
                    d="M16 6 L16 26 M6 16 L26 16"
                    stroke={akru.orange}
                    strokeWidth="2"
                  />
                  <chakra.circle cx="16" cy="6" r="2" fill={akru.orange} />
                  <chakra.circle cx="16" cy="26" r="2" fill={akru.orange} />
                  <chakra.circle cx="6" cy="16" r="2" fill={akru.orange} />
                  <chakra.circle cx="26" cy="16" r="2" fill={akru.orange} />
                </Box>
              }
            />

            <ProductCard
              title="Tokenized Credit Participation Pools"
              bullets={[
                "Create tokenized, compliant credit pools to sell participations off balance sheet",
                "Expand lending capacity and tap institutional marketplaces like Coinbase RWA or Securitize",
              ]}
              icon={
                <Box
                  as="svg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="28"
                  height="35"
                  viewBox="0 0 28 35"
                  fill="none"
                >
                  <chakra.rect
                    x="2"
                    y="15"
                    width="8"
                    height="18"
                    stroke={akru.orange}
                    strokeWidth="2"
                  />
                  <chakra.rect
                    x="10"
                    y="10"
                    width="8"
                    height="23"
                    stroke={akru.orange}
                    strokeWidth="2"
                  />
                  <chakra.rect
                    x="18"
                    y="5"
                    width="8"
                    height="28"
                    stroke={akru.orange}
                    strokeWidth="2"
                  />
                </Box>
              }
            />
          </Grid>

          <Flex justify="center">
            <Button
              as="a"
              href="#pilots"
              bg={akru.orange}
              color={akru.white}
              fontWeight="700"
              fontSize="16px"
              px="26px"
              py="13px"
              borderRadius={akru.radius}
              _hover={{ bg: akru.orangeHover }}
            >
              Learn More About Our Credit Union Pilots
            </Button>
          </Flex>
        </Box>
      </Box>

      {/* WHY IT MATTERS */}
      <Box as="section" bg={akru.offWhite} py={{ base: "80px", md: "130px" }}>
        <Box maxW={akru.maxW} mx="auto" px={{ base: "20px", md: "40px" }}>
          <Heading
            as="h2"
            textAlign="center"
            fontSize={{ base: "40px", md: "64px" }}
            fontWeight="500"
            lineHeight={{ base: "50px", md: "70px" }}
            color={akru.darkText}
            mb="30px"
          >
            Why It Matters
          </Heading>

          <Text
            textAlign="center"
            fontSize={{ base: "16px", md: "20px" }}
            fontWeight="500"
            color={akru.grayText}
            maxW="768px"
            mx="auto"
            mb="60px"
            lineHeight={{ base: "28px", md: "40px" }}
          >
            Transforming operations for credit unions and unlocking opportunities
            for members
          </Text>

          <Grid
            gap={{ base: "40px", md: "80px" }}
            templateColumns={{ base: "1fr", md: "repeat(auto-fit,minmax(400px,1fr))" }}
            mt="60px"
          >
            <Box
              bg={akru.white}
              p="50px 40px"
              borderRadius={akru.radius}
              boxShadow={akru.shadow}
            >
              <Heading
                as="h3"
                fontSize="36px"
                fontWeight="600"
                mb="30px"
                pb="20px"
                lineHeight="65px"
                borderBottom="3px solid"
                borderColor={akru.orange}
                color={akru.darkText}
              >
                For Credit Unions
              </Heading>
              <List spacing="0" styleType="none" m="0" p="0">
                {[
                  "Balance sheet relief and liquidity flexibility",
                  "New fee-based revenue streams",
                  "Shorter settlement and funding cycles",
                ].map((t, i) => (
                  <ListItem
                    key={i}
                    py="18px"
                    borderBottom="1px solid"
                    borderColor={akru.borderGray}
                    fontSize="20px"
                    lineHeight="28px"
                    fontWeight="500"
                    color={akru.grayText}
                  >
                    {t}
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box
              bg={akru.white}
              p="50px 40px"
              borderRadius={akru.radius}
              boxShadow={akru.shadow}
            >
              <Heading
                as="h3"
                fontSize="36px"
                fontWeight="600"
                mb="30px"
                pb="20px"
                lineHeight="65px"
                borderBottom="3px solid"
                borderColor={akru.orange}
                color={akru.darkText}
              >
                For Members
              </Heading>
              <List spacing="0" styleType="none" m="0" p="0">
                {[
                  "Access to new, compliant investment opportunities",
                  "Participation in local and sustainable lending programs",
                ].map((t, i) => (
                  <ListItem
                    key={i}
                    py="18px"
                    borderBottom="1px solid"
                    borderColor={akru.borderGray}
                    fontSize="20px"
                    lineHeight="28px"
                    fontWeight="500"
                    color={akru.grayText}
                  >
                    {t}
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>
        </Box>
      </Box>

      {/* COMPLIANCE */}
      <Box as="section" bg={akru.white} py={{ base: "80px", md: "130px" }}>
        <Box maxW={akru.maxW} mx="auto" px={{ base: "20px", md: "40px" }}>
          <Box textAlign="center" maxW="900px" mx="auto">
            <Heading
              as="h2"
              fontSize={{ base: "40px", md: "64px" }}
              fontWeight="500"
              lineHeight={{ base: "50px", md: "70px" }}
              color={akru.darkText}
              mb="30px"
            >
              Compliance First. Always.
            </Heading>

            <Text
              fontSize={{ base: "16px", md: "24px" }}
              lineHeight={{ base: "28px", md: "40px" }}
              fontWeight="500"
              mb="50px"
              color={akru.grayText}
            >
              AKRU&apos;s infrastructure is built on the ERC-1400/3643 framework with
              full KYC/AML integration, audit logs, and permissioned transfers.
              Designed to pass credit union vendor risk assessments, IT due
              diligence, and regulatory reporting standards.
            </Text>

            <Grid
              gap="30px"
              templateColumns={{
                base: "repeat(2,1fr)",
                md: "repeat(auto-fit,minmax(200px,1fr))",
              }}
              mt="50px"
            >
              <ComplianceBadge
                label="ERC-1400/3643"
                icon={
                  <Box
                    as="svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <chakra.path
                      d="M12 2L3 7V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V7L12 2Z"
                      stroke={akru.orange}
                      strokeWidth="2"
                    />
                  </Box>
                }
              />
              <ComplianceBadge
                label="KYC/AML"
                icon={
                  <Box
                    as="svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <chakra.rect
                      x="5"
                      y="11"
                      width="14"
                      height="10"
                      stroke={akru.orange}
                      strokeWidth="2"
                    />
                    <chakra.path
                      d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
                      stroke={akru.orange}
                      strokeWidth="2"
                    />
                  </Box>
                }
              />
              <ComplianceBadge
                label="Audit Logs"
                icon={
                  <Box
                    as="svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <chakra.path
                      d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke={akru.orange}
                      strokeWidth="2"
                    />
                  </Box>
                }
              />
              <ComplianceBadge
                label="Permissioned Transfers"
                icon={
                  <Box
                    as="svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <chakra.path
                      d="M12 15V21M9 18H15M4 8H20M4 8V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V8M4 8L8 3M20 8L16 3"
                      stroke={akru.orange}
                      strokeWidth="2"
                    />
                  </Box>
                }
              />
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* PILOT PROGRAMS */}
      <Box
        as="section"
        id="pilots"
        bg={akru.offWhite}
        py={{ base: "80px", md: "130px" }}
      >
        <Box maxW={akru.maxW} mx="auto" px={{ base: "10px", md: "40px" }}>
          <Heading
            as="h2"
            fontSize={{ base: "40px", md: "64px" }}
            fontWeight="500"
            lineHeight={{ base: "50px", md: "70px" }}
            textAlign="center"
            color={akru.darkText}
            mb="30px"
          >
            Pilot Programs with
            <br />
            Measurable Results
          </Heading>

          <Text
            textAlign="center"
            fontSize={{ base: "16px", md: "20px" }}
            fontWeight="500"
            color={akru.grayText}
            maxW="768px"
            mx="auto"
            mb="60px"
            lineHeight={{ base: "28px", md: "40px" }}
          >
            Choose a pilot program tailored to your credit union&apos;s specific
            needs and goals
          </Text>

          <Grid
            gap="40px"
            templateColumns={{ base: "1fr", md: "repeat(auto-fit,minmax(320px,1fr))" }}
            mb={{base:"20px",md:"60px"}}
          >
            <PilotCard
              number="PILOT 01"
              title="TreasuryConnect Pilot"
              duration="Duration: 60–90 Days"
              bullets={[
                "Launch fintech-ready treasury services",
                "Real-time reconciliation and settlement metrics",
              ]}
            />
            <PilotCard
              number="PILOT 02"
              title="Small-Asset Tokenization Pilot"
              duration="Duration: 90 Days"
              bullets={[
                "Liquidity activation for small loans",
                "Capital efficiency improvement tracking",
              ]}
            />
            <PilotCard
              number="PILOT 03"
              title="Tokenized Credit Pool Pilot"
              duration="Duration: 90 Days"
              bullets={[
                "Curql-aligned proof of balance-sheet relief",
                "Participation marketplace integration",
              ]}
            />
          </Grid>
        </Box>
      </Box>

      {/* FINAL CTA */}
      <Box
        as="section"
        id="contact"
        textAlign="center"
        bgGradient={`linear(135deg, rgba(253,133,52,0.05) 0%, rgba(255,255,255,1) 100%)`}
        color={akru.darkText}
        py={{ base: "80px", md: "130px" }}
      >
        <Box maxW={akru.maxW} mx="auto" px={{ base: "20px", md: "40px" }}>
          <Heading
            as="h2"
            fontSize={{ base: "32px", md: "48px" }}
            fontWeight="400"
            lineHeight={{ base: "45px", md: "65px" }}
            mb="30px"
            color={akru.darkText}
          >
            Let&apos;s Build the Future of
            <br />
            Credit Union Finance Together
          </Heading>

          <Text
            fontSize={{ base: "16px", md: "24px" }}
            fontWeight="500"
            lineHeight={{ base: "28px", md: "40px" }}
            mb="50px"
            maxW="700px"
            mx="auto"
            color={akru.grayText}
          >
            Explore how AKRU&apos;s compliant tokenization and treasury
            infrastructure can future-proof your institution.
          </Text>

          <Stack
            direction={{ base: "column", md: "row" }}
            spacing="20px"
            justify="center"
          >
            <Button
              as="a"
              href="mailto:partnerships@akruonfi.com"
              bg={akru.orange}
              color={akru.white}
              fontWeight="700"
              fontSize="16px"
              px="26px"
              py="13px"
              borderRadius={akru.radius}
              _hover={{ bg: akru.orangeHover }}
            >
              Schedule a Call
            </Button>

            <Button
              as="a"
              href="#"
              bg={akru.white}
              color={akru.orange}
              fontWeight="700"
              fontSize="16px"
              px="26px"
              py="13px"
              borderRadius={akru.radius}
              border="2px solid"
              borderColor={akru.orange}
              _hover={{ bg: akru.orangeLight }}
            >
              Download Credit Union Briefs
            </Button>
          </Stack>

          <Text
            mt="40px"
            fontSize="14px"
            fontWeight="500"
            color={akru.grayText}
            fontStyle="italic"
          >
            All AKRU solutions are regulatory-compliant and integrate without core
            replacement.
          </Text>
        </Box>
      </Box>

     
    </Box>
  );
}
export default AkruCreditUnions;